import React, { useEffect, useMemo, useRef, useState } from 'react';
import { requestReceivedCopy, requestReceivedText } from '../lib/requestReceivedCopy';
import { useNavigate } from 'react-router-dom';
import { sb } from '../supabase';
import { generateIdeaToProductReport, requestProductConversationReply } from '../lib/maabarAi/client';
import { MAABAR_AI_PERSONA_NAME } from '../lib/maabarAi/config';
import {
  buildIdeaRequestDraft,
  clearIdeaFlowDraft,
  ideaDraftMissing,
  ideaQuantityInvalid,
  loadIdeaFlowDraft,
  saveIdeaFlowDraft,
} from '../lib/ideaToProductFlow';
import { normalizeQuantityInput, quantityMessage } from '../lib/requestQuantity';
import { buildManagedBriefRow, generateManagedBriefWithAI } from '../lib/managedSourcing';
import { runWithOptionalColumns } from '../lib/supabaseColumnFallback';
import useRequestAttachments from '../hooks/useRequestAttachments';
import Notice from './Notice';
import PhoneField, { parseStoredPhone } from './PhoneField';
import VerifiedPhoneField, { requestPhoneRefusal, phoneRefusalMessage } from './VerifiedPhoneField';
import { saveProfilePhoneIfBlank } from '../lib/profilePhone';
import RequestFeeNotice from './RequestFeeNotice';
import { fetchFeeConfig, payFeeIfRequired } from '../lib/requestFee';
import { ATTACH_ACCEPT, REQUEST_UNITS, DELIVERY_PREFERENCES, labelFor, deliveryHintFor } from '../lib/requestFormOptions';
import CityPicker from './CityPicker';
import { UI_CATEGORIES, getSpecialtyLabel } from '../lib/supplierDashboardConstants';
import {
  DEFAULT_DISPLAY_CURRENCY,
  normalizeDisplayCurrency,
} from '../lib/displayCurrency';
import { requestContactNote } from '../lib/requestContactNote';
import useServiceTracking from '../hooks/useServiceTracking';

const SAUDI_REPRESENTATIVE_NAMES = [
  'سلمان', 'فيصل', 'تركي', 'عبدالعزيز', 'سعود', 'نورة', 'العنود', 'الجوهرة', 'ريم', 'لولوة'
];

// Category labels/options come from lib/supplierDashboardConstants
// (UI_CATEGORIES — trilingual, 23; getSpecialtyLabel for single-code display).

// Exactly two plans since 2026-09-28 (owner): 30 (deposit) and 100 (full).
// This map IS the review screen's picker (Object.entries below), so 50 is gone
// from «صنّع فكرتك» with it.
const PAYMENT_PLAN_LABEL = {
  ar: {
    30: '30% مقدماً',
    100: '100% مقدماً',
  },
  en: {
    30: '30% upfront',
    100: '100% upfront',
  },
  zh: {
    30: '预付 30%',
    100: '预付 100%',
  },
};

const SAMPLE_LABEL = {
  ar: {
    none: 'لا حاجة لعينة',
    preferred: 'عينة مفضلة إن توفرت',
    required: 'عينة إلزامية',
  },
  en: {
    none: 'No sample needed',
    preferred: 'Sample preferred if available',
    required: 'Sample is mandatory',
  },
  zh: {
    none: '无需样品',
    preferred: '如方便可提供样品',
    required: '必须提供样品',
  },
};

// صنّع فكرتك — the trader's two options (owner, 2026-09-29). Arabic and
// English only: this is trader copy, and a 'zh' reader gets English.
//   publish      free — goes to suppliers as a plain RFQ (request_kind 'direct')
//   team_search  the Maabar team finds the factory — carries the request fee
const IDEA_MODE_COPY = {
  ar: {
    heading: 'كيف تبغى نكمل طلبك؟',
    publish: 'انشر طلبك على المنصة',
    publishPrice: 'مجانًا',
    publishBody: 'يوصل طلبك للموردين المناسبين ويرسلون لك عروضهم مباشرة.',
    team: 'فريق مَعبر يبحث لك عن مصنع',
    teamBody: 'فريقنا يبحث بنفسه ويتواصل مع المصانع نيابةً عنك.',
    required: 'اختر طريقة إكمال طلبك.',
  },
  en: {
    heading: 'How should we take your request forward?',
    publish: 'Publish your request on the platform',
    publishPrice: 'Free',
    publishBody: 'Your request reaches the right suppliers and they send you their quotes directly.',
    team: 'Maabar team finds a factory for you',
    teamBody: 'Our team actively searches and contacts factories on your behalf.',
    required: 'Choose how to take your request forward.',
  },
};

const COPY = {
  ar: {
    title: 'صنّع فكرتك',
    subtitle: `${MAABAR_AI_PERSONA_NAME} يحوّل الفكرة إلى طلب تصنيع احترافي`,
    minimized: 'صنّع فكرتك — متابعة',
    placeholder: 'اكتب رسالتك هنا...',
    send: 'إرسال',
    generating: 'أرتب فكرتك الآن وأبني لك brief احترافي...',
    report: 'تقرير التصنيع',
    review: 'راجع الطلب قبل الإرسال',
    reviewHint: 'هذا هو الطلب الذي سيصل للموردين المطابقين لتخصصك. عدّل فقط ما يلزم ثم أرسله.',
    submit: 'إرسال لموردين مختصين',
    loginSubmit: 'تسجيل الدخول للإرسال',
    newChat: 'محادثة جديدة',
    error: 'حدث خطأ، حاول مرة أخرى',
    attachLabel: 'صور ومرفقات مرجعية (اختياري)',
    attachHint: 'صور، PDF، ZIP — حتى 6 ملفات، 10 ميجابايت لكل ملف. لا تُحفظ مع المسودة.',
    attachPick: 'أضف صوراً أو ملفات',
    attachChat: 'إرفاق صور أو ملفات',
    attachedInChat: (n) => `مرفق مع طلبك: ${n}`,
    attachRemove: 'إزالة',
    warnUpload: (n) => `أُرسل طلبك، لكن تعذّر رفع: ${n}. يمكنك إضافتها لاحقاً من صفحة الطلب.`,
    reportReady: 'جهزت لك التقرير. راجعه، وإذا مناسب أرسله لموردين مختصين مباشرة.',
    supplierRouteIntro: 'واضح أنك تريد الوصول لموردين بسرعة. خلني آخذ منك الحد الأدنى فقط ثم أجهز الطلب بشكل احترافي.',
    buildRouteIntro: 'ممتاز. خلني أفهم فكرتك بشكل مرتب، وبعدها أطلع لك brief جاهز للتصنيع.',
    budgetOptional: 'الميزانية للوحدة',
    requestPreviewNote: 'الإرسال ينشر طلبك المفتوح تحت هذا التصنيف حتى يراه الموردون المناسبون.',
    reportFields: {
      product: 'المنتج',
      factory: 'نوع المصنع',
      city: 'المدينة',
      cost: 'تكلفة تقريبية',
      moq: 'MOQ',
      timeline: 'مدة التصنيع',
      category: 'التخصص',
      specs: 'المواصفات',
    },
    reviewFields: {
      titleAr: 'اسم المنتج بالعربي *',
      titleEn: 'اسم المنتج بالإنجليزي',
      quantity: 'الكمية المطلوبة *',
      category: 'التصنيف',
      description: 'تفاصيل الطلب',
      paymentPlan: 'خطة الدفع *',
      sampleRequirement: 'متطلبات العينة *',
      shipCity: 'مدينة التسليم *',
      cityPlaceholder: 'اختر المدينة',
      delivery: 'كيف تبي تستلم البضاعة؟ *',
    },
    requiredDraft: 'يرجى تعبئة اسم المنتج والكمية ووحدتها وخطة الدفع ومتطلبات العينة ومدينة التسليم وطريقة الاستلام',
    moqHint: 'تقدير الوكيل للحد الأدنى للطلب:',
    unitPlaceholder: 'الوحدة',
    phoneLabel: 'رقم الجوال (واتساب) *',
    phoneHint: 'ليتواصل معك مدير حسابك في معبر بخصوص الطلب.',
    errPhone: 'يرجى إدخال رقم جوال صحيح للتواصل.',
    errPhoneVerify: 'يرجى توثيق رقم جوالك عبر واتساب قبل إرسال الطلب.',
  },
  en: {
    title: 'Build Your Product',
    subtitle: `${MAABAR_AI_PERSONA_NAME} turns your idea into a professional manufacturing brief`,
    minimized: 'Build Your Product — Continue',
    placeholder: 'Write your message...',
    send: 'Send',
    generating: 'Preparing your manufacturing brief...',
    report: 'Manufacturing Report',
    review: 'Review before sending',
    reviewHint: 'This is the request that will be sent to the best-matched suppliers. Adjust anything important, then send it.',
    submit: 'Send to matched suppliers',
    loginSubmit: 'Login to submit',
    newChat: 'New Chat',
    error: 'Something went wrong, please try again',
    attachLabel: 'Reference images & files (optional)',
    attachHint: 'Images, PDF, ZIP — up to 6 files, 10 MB each. Not saved with a draft.',
    attachPick: 'Add images or files',
    attachChat: 'Attach images or files',
    attachedInChat: (n) => `Attached to your request: ${n}`,
    attachRemove: 'Remove',
    warnUpload: (n) => `Your request was sent, but these did not upload: ${n}. You can add them later from the request page.`,
    reportReady: 'Your report is ready. Review it and send it to matched suppliers when ready.',
    supplierRouteIntro: 'Got it — you want supplier matching fast. I’ll only ask for the essentials, then prepare a clean brief.',
    buildRouteIntro: 'Great. I’ll understand your idea first, then turn it into a professional manufacturing brief.',
    budgetOptional: 'Budget per unit',
    requestPreviewNote: 'Sending publishes your request under this category so the right suppliers can see it.',
    reportFields: {
      product: 'Product',
      factory: 'Factory Type',
      city: 'City',
      cost: 'Est. Cost',
      moq: 'MOQ',
      timeline: 'Timeline',
      category: 'Category',
      specs: 'Specifications',
    },
    reviewFields: {
      titleAr: 'Product Name (AR) *',
      titleEn: 'Product Name (EN)',
      quantity: 'Required Quantity *',
      category: 'Category',
      description: 'Request Details',
      paymentPlan: 'Payment Plan *',
      sampleRequirement: 'Sample Requirement *',
      shipCity: 'Delivery city *',
      cityPlaceholder: 'Select a city',
      delivery: 'How do you want to receive the goods? *',
    },
    requiredDraft: 'Add product name, quantity and unit, payment plan, sample requirement, delivery city and delivery method',
    moqHint: 'AI estimate of the minimum order:',
    unitPlaceholder: 'Unit',
    phoneLabel: 'Mobile number (WhatsApp) *',
    phoneHint: 'So your Maabar account manager can reach you about this request.',
    errPhone: 'Please enter a valid contact mobile number.',
    errPhoneVerify: 'Please verify your mobile number via WhatsApp before sending the request.',
  },
  zh: {
    title: '打造您的产品',
    subtitle: `${MAABAR_AI_PERSONA_NAME} 将想法整理成专业制造需求`,
    minimized: '继续打造产品',
    placeholder: '请输入内容...',
    send: '发送',
    generating: '正在整理您的制造需求...',
    report: '制造报告',
    review: '发送前确认',
    reviewHint: '这份需求将发送给最匹配的供应商。请先确认并修改必要内容，再发送。',
    submit: '发送给匹配供应商',
    loginSubmit: '登录后发送',
    newChat: '新对话',
    error: '出错了，请重试',
    attachLabel: '参考图片与附件（可选）',
    attachHint: '图片、PDF、ZIP — 最多 6 个文件，每个 10 MB。不会随草稿保存。',
    attachPick: '添加图片或文件',
    attachChat: '附加图片或文件',
    attachedInChat: (n) => `已附加到您的需求：${n}`,
    attachRemove: '删除',
    warnUpload: (n) => `需求已发送，但以下文件未能上传：${n}。您可以稍后在需求页面补充。`,
    reportReady: '报告已准备好。确认后可直接发送给匹配供应商。',
    supplierRouteIntro: '了解，您想尽快对接供应商。我先收集最核心的信息，再为您整理成专业需求。',
    buildRouteIntro: '很好。我先了解您的产品想法，然后整理成专业制造 brief。',
    budgetOptional: '单价预算',
    requestPreviewNote: '发送后会按当前分类发布需求，方便匹配供应商查看。',
    reportFields: {
      product: '产品',
      factory: '工厂类型',
      city: '城市',
      cost: '预估成本',
      moq: 'MOQ',
      timeline: '生产周期',
      category: '类别',
      specs: '规格要求',
    },
    reviewFields: {
      titleAr: '阿拉伯语产品名 *',
      titleEn: '英语产品名',
      quantity: '需求数量 *',
      category: '类别',
      description: '需求详情',
      paymentPlan: '付款方案 *',
      sampleRequirement: '样品要求 *',
      shipCity: '收货城市 *',
      cityPlaceholder: '选择城市',
      delivery: '您希望如何收货？ *',
    },
    requiredDraft: '请填写产品名称、数量及单位、付款方案、样品要求、收货城市和收货方式',
    moqHint: 'AI 估算的起订量：',
    unitPlaceholder: '单位',
    phoneLabel: '手机号码（WhatsApp）*',
    phoneHint: '便于 Maabar 客户经理就此需求与您联系。',
    errPhone: '请输入有效的联系手机号码。',
    errPhoneVerify: '提交需求前，请先通过 WhatsApp 验证您的手机号码。',
  },
};

function detectIntent(text = '') {
  const t = text.toLowerCase();
  const supplierKeywords = [
    'مورد', 'موردين', 'مصنع', 'مصانع', 'وصلني', 'ابي مورد', 'أبي مورد', 'ابي اوصل', 'أبي أوصل',
    'supplier', 'suppliers', 'factory', 'factories', 'manufacturer', 'sourcing', 'connect me',
    '供应商', '工厂', '对接', '找工厂'
  ];
  return supplierKeywords.some((keyword) => t.includes(keyword)) ? 'supplier_match' : 'build_product';
}

function Bubble({ role, children, isAr }) {
  const isUser = role === 'user';
  return (
    <div style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
      <div style={{
        maxWidth: '86%',
        padding: '14px 16px',
        borderRadius: 'var(--radius-card)',
        background: isUser ? 'var(--bg-hero)' : 'var(--surface-raised)',
        color: isUser ? 'var(--ink)' : 'var(--text-primary)',
        border: `1px solid ${isUser ? 'var(--border)' : 'var(--border)'}`,
        fontSize: 14,
        lineHeight: 1.9,
        fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
        whiteSpace: 'pre-wrap',
        boxShadow: 'none',
      }}>
        {children}
      </div>
    </div>
  );
}

function getInitialMessages(language = 'ar', representativeName = 'سلمان') {
  if (language === 'zh') {
    return [
      `您好，我是来自 Maabar 的 ${representativeName}。`,
      '请告诉我您的想法。',
    ];
  }

  if (language === 'en') {
    return [
      `Hello, this is ${representativeName} from Maabar.`,
      'What do you have in mind?',
    ];
  }

  return [
    `مرحبا، معك ${representativeName} من معبر.`,
    'كيف اقدر اساعدك ؟',
  ];
}

function TypingBubble() {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
      <div style={{ maxWidth: '86%', padding: '14px 16px', borderRadius: 'var(--radius-card)', background: 'var(--surface-raised)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {[0, 1, 2].map((dot) => (
            <span key={dot} style={{ width: 8, height: 8, borderRadius: 'var(--radius-full)', background: 'var(--text-disabled)', display: 'inline-block', opacity: 0.35, animation: `maabarTyping 1.2s ${dot * 0.15}s infinite ease-in-out` }} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ReportRow({ label, value, isAr }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ color: 'var(--text-secondary)', fontSize: 12, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{label}</span>
      <span style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 500, textAlign: isAr ? 'left' : 'right', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{value}</span>
    </div>
  );
}

function FormLabel({ children, isAr }) {
  return (
    <p style={{ color: 'var(--text-secondary)', fontSize: 11, marginBottom: 8, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
      {children}
    </p>
  );
}

function FieldInput({ isAr, as = 'input', ...props }) {
  const Comp = as;
  return (
    <Comp
      {...props}
      style={{
        width: '100%',
        background: 'var(--bg-page)',
        color: 'var(--text-primary)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-panel)',
        padding: '12px 14px',
        fontSize: 16,
        lineHeight: 1.8,
        outline: 'none',
        resize: as === 'textarea' ? 'vertical' : 'none',
        fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
        direction: isAr ? 'rtl' : 'ltr',
        ...props.style,
      }}
    />
  );
}

// The visitor's card (2026-10-22). Same words as the app's.
const IDEA_GATE = {
  ar: { title: 'سجّل دخولك عشان تستخدم «صنّع فكرتك»', body: 'وكيل معبر يحوّل فكرتك إلى طلب تصنيع جاهز للمصانع. الميزة للمسجّلين فقط — التسجيل مجاني ويأخذ دقيقة.', signIn: 'تسجيل الدخول', signUp: 'إنشاء حساب جديد', back: 'رجوع' },
  en: { title: 'Sign in to use «Make your idea»', body: "Maabar's agent turns your idea into a manufacturing request ready for factories. It is for signed-in traders — signing up is free and takes a minute.", signIn: 'Sign in', signUp: 'Create an account', back: 'Back' },
  zh: { title: '登录后即可使用「定制你的创意」', body: 'Maabar 代理将您的创意转化为可发给工厂的制造请求。此功能仅限登录用户——注册免费，只需一分钟。', signIn: '登录', signUp: '创建账户', back: '返回' },
};

export default function IdeaToProduct({ lang, user, profile, setProfile, onClose, displayCurrency }) {
  const nav = useNavigate();
  const isAr = lang === 'ar';
  const t = COPY[lang] || COPY.en;
  // The Saudi trader is the primary user of this flow — default to SAR.
  // displayCurrency (if plumbed from App-level state) overrides; user can
  // still change it on the form. Stored AS-ENTERED, never converted.
  const defaultBudgetCurrency = normalizeDisplayCurrency(displayCurrency || 'SAR');
  const representativeName = useMemo(() => SAUDI_REPRESENTATIVE_NAMES[Math.floor(Math.random() * SAUDI_REPRESENTATIVE_NAMES.length)], []);
  const initialMessages = useMemo(() => getInitialMessages(lang, representativeName), [lang, representativeName]);
  const [minimized, setMinimized] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
  const bodyRef = useRef(null);
  const [draftText, setDraftText] = useState('');
  const [phase, setPhase] = useState('chat');
  const [idea, setIdea] = useState('');
  const [result, setResult] = useState(null);
  const [reviewDraft, setReviewDraft] = useState(() => buildIdeaRequestDraft({}, { budget_currency: defaultBudgetCurrency }));
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // '' until the trader picks: 'publish' (free RFQ) or 'team_search' (fee).
  const [ideaMode, setIdeaMode] = useState('');
  const [feeConfig, setFeeConfig] = useState(null);
  useEffect(() => { fetchFeeConfig(sb).then(setFeeConfig); }, []);
  // Growth OS (step 7): «صنّع فكرتك» — chat, then publish (free) or team search (fee).
  const svc = useServiceTracking('idea');
  useEffect(() => { if (ideaMode) svc.step(2, ideaMode === 'team_search' ? 'mode_team_search' : 'mode_publish'); }, [ideaMode, svc]);
  useEffect(() => { if (ideaMode === 'team_search' && feeConfig?.enabled && feeConfig.amount) svc.feeShown(); }, [ideaMode, feeConfig, svc]);
  const mt = IDEA_MODE_COPY[lang === 'ar' ? 'ar' : 'en'];

  // persistKey: files survive the sign-in redirect with the idea draft (owner,
  // 2026-10-17) — see src/lib/draftFiles.js.
  const picker = useRequestAttachments(lang, { persistKey: 'idea-to-product' });
  // Filenames that did not upload. Non-empty means the request was created
  // ANYWAY and the trader is being told what to re-add.
  //
  // This flow persists its draft (saveIdeaFlowDraft) across the sign-in
  // redirect, and a File cannot be serialized — so picked files do not survive
  // it either. The attachHint says so inline rather than after the fact, which
  // is the cheaper place to say it: unlike the wizard, this panel is dismissed
  // and reopened, so a restore notice would fire on every reopen.
  const [failedFiles, setFailedFiles] = useState([]);
  // Contact phone → requests.contact_phone. REQUIRED (2026-09-24): M-10089
  // arrived with no contact_phone and a profile with no phone, and the
  // concierge had no way to reach the trader. Prefilled from profiles.phone
  // when there is one; NOT written back to the profile — only the Settings
  // page writes profiles.phone, on an explicit save. Kept out of the stored
  // draft on purpose: that draft sits in localStorage.
  const [phone, setPhone] = useState(() => parseStoredPhone(profile?.phone));
  const [phoneKey, setPhoneKey] = useState(0);
  const phoneTouched = useRef(false);
  useEffect(() => {
    // The profile can arrive after mount (sign-in redirect back into the
    // flow). PhoneField reads its value once, so remount it with the prefill —
    // unless the trader has already typed.
    if (phoneTouched.current || !profile?.phone) return;
    setPhone(parseStoredPhone(profile.phone));
    setPhoneKey((k) => k + 1);
  }, [profile?.phone]);
  // team_search only (owner, 2026-10-01): the number must be VERIFIED by a
  // WhatsApp code — the server refuses an idea team_search request otherwise
  // (enforce_verified_request_phone). verifiedPhone is VerifiedPhoneField's
  // confirmed number or ''; the plain `phone` above stays for 'publish'.
  const [verifiedPhone, setVerifiedPhone] = useState('');
  // { hint, n } when the insert came back phone_required / phone_not_verified.
  const [phoneRefusal, setPhoneRefusal] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [briefReady, setBriefReady] = useState(false);
  const [messages, setMessages] = useState(() =>
    initialMessages.map((content, index) => ({ id: index + 1, role: 'assistant', content }))
  );

  useEffect(() => {
    const storedDraft = loadIdeaFlowDraft();
    if (!storedDraft) return;

    const storedMessages = Array.isArray(storedDraft.messages) && storedDraft.messages.length > 0
      ? storedDraft.messages
      : initialMessages.map((content, index) => ({ id: Date.now() + index, role: 'assistant', content }));

    setMessages(storedMessages.map((message, index) => ({
      id: message.id || Date.now() + index,
      role: message.role,
      content: message.content,
    })));
    setIdea(storedDraft.initialIdea || '');
    setResult(storedDraft.report || null);
    setReviewDraft(buildIdeaRequestDraft(storedDraft.report || {}, storedDraft.requestDraft || {}));
    setBriefReady(Boolean(storedDraft.report));
    setPhase(storedDraft.report ? 'report' : (storedDraft.phase || 'chat'));
  }, [initialMessages]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isMobile) return undefined;

    const scrollY = window.scrollY;
    const htmlStyle = document.documentElement.style;
    const bodyStyle = document.body.style;
    const prevHtmlOverflow = htmlStyle.overflow;
    const prevBodyOverflow = bodyStyle.overflow;
    const prevBodyPosition = bodyStyle.position;
    const prevBodyTop = bodyStyle.top;
    const prevBodyWidth = bodyStyle.width;

    htmlStyle.overflow = 'hidden';
    bodyStyle.overflow = 'hidden';
    bodyStyle.position = 'fixed';
    bodyStyle.top = `-${scrollY}px`;
    bodyStyle.width = '100%';

    return () => {
      htmlStyle.overflow = prevHtmlOverflow;
      bodyStyle.overflow = prevBodyOverflow;
      bodyStyle.position = prevBodyPosition;
      bodyStyle.top = prevBodyTop;
      bodyStyle.width = prevBodyWidth;
      window.scrollTo(0, scrollY);
    };
  }, [isMobile]);

  useEffect(() => {
    if (!bodyRef.current) return;
    bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, isTyping, phase]);

  const resetAll = () => {
    clearIdeaFlowDraft();
    setDraftText('');
    setPhase('chat');
    setIdea('');
    setResult(null);
    setReviewDraft(buildIdeaRequestDraft({}, { budget_currency: defaultBudgetCurrency }));
    setError('');
    setSubmitting(false);
    setIsTyping(false);
    setBriefReady(false);
    setMessages(initialMessages.map((content, index) => ({ id: Date.now() + index, role: 'assistant', content })));
    // A new chat is a new request: files picked for the old one must not ride
    // into it. (Before the chat had its own button this was easy to miss.)
    picker.reset();
    setFailedFiles([]);
  };

  const appendMessage = (role, content) => {
    setMessages((prev) => [...prev, { id: Date.now() + Math.random(), role, content }]);
  };

  const persistFlowDraft = ({ report = result, nextReviewDraft = reviewDraft, nextMessages = messages, nextPhase = phase, pendingAuth = !user, initialIdea = idea }) => {
    saveIdeaFlowDraft({
      lang,
      phase: nextPhase,
      report,
      requestDraft: nextReviewDraft,
      messages: nextMessages,
      initialIdea,
      pendingAuth,
    });
  };

  const generateReport = async (sourceOverride = '', messageOverride = null) => {
    const conversationMessages = Array.isArray(messageOverride) ? messageOverride : messages;
    const userConversation = conversationMessages
      .filter((message) => message.role === 'user')
      .map((message) => message.content)
      .join('\n');

    const sourceIdea = sourceOverride || userConversation || idea;
    if (!sourceIdea.trim()) return;

    setPhase('generating');
    setError('');
    try {
      const report = await generateIdeaToProductReport({
        language: lang,
        mode: detectIntent(sourceIdea),
        initialIdea: sourceIdea,
        questions: [],
        answers: [],
      });
      const nextMessages = [...conversationMessages, { id: Date.now() + Math.random(), role: 'assistant', content: t.reportReady }];
      const nextReviewDraft = buildIdeaRequestDraft(report, reviewDraft);
      setResult(report);
      setReviewDraft(nextReviewDraft);
      setMessages(nextMessages);
      setPhase('report');
      setBriefReady(true);
      persistFlowDraft({
        report,
        nextReviewDraft,
        nextMessages,
        nextPhase: 'report',
        pendingAuth: !user,
        initialIdea: sourceIdea,
      });
    } catch (_error) {
      setError(t.error);
      setPhase('chat');
      appendMessage('assistant', t.error);
    }
  };

  const handleSend = async () => {
    svc.start();
    svc.step(1, 'chat');
    const value = draftText.trim();
    if (!value || isTyping) return;

    const nextConversation = messages.map((message) => ({ role: message.role, content: message.content }));
    const nextMessagesWithUser = [...messages, { id: Date.now() + Math.random(), role: 'user', content: value }];
    setDraftText('');
    setIdea((prev) => prev || value);
    setMessages(nextMessagesWithUser);
    setIsTyping(true);
    setError('');

    try {
      const productReply = await requestProductConversationReply({
        language: lang,
        conversation: nextConversation,
        userMessage: value,
        userProfile: {
          role: 'buyer',
          representativeName,
        },
      });
      const cleanReply = lang === 'ar'
        ? String(productReply?.reply || t.error).replace(/[\u3400-\u9FFF]+/g, '').replace(/\s{2,}/g, ' ').trim()
        : (productReply?.reply || t.error);
      const nextMessages = [...nextMessagesWithUser, { id: Date.now() + Math.random(), role: 'assistant', content: cleanReply || t.error }];
      setMessages(nextMessages);
      const readyForBrief = Boolean(productReply?.enoughInfo || ['brief_ready', 'supplier_ready'].includes(productReply?.nextStep));
      setBriefReady(readyForBrief);
      if (readyForBrief) {
        await generateReport(
          [...nextConversation.filter((message) => message.role === 'user').map((message) => message.content), value].join('\n'),
          nextMessages,
        );
      } else {
        persistFlowDraft({ nextMessages, nextPhase: 'chat', initialIdea: idea || value });
      }
    } catch (_error) {
      const nextMessages = [...nextMessagesWithUser, { id: Date.now() + Math.random(), role: 'assistant', content: t.error }];
      setMessages(nextMessages);
      setError(t.error);
    } finally {
      setIsTyping(false);
    }
  };

  const updateReviewDraft = (field, value) => {
    setReviewDraft((prev) => {
      const nextDraft = { ...prev, [field]: value };
      persistFlowDraft({ nextReviewDraft: nextDraft, nextPhase: 'report' });
      return nextDraft;
    });
  };

  const submitRequest = async () => {
    const normalizedDraft = buildIdeaRequestDraft(result || {}, reviewDraft || {});
    // A typed-but-not-plain quantity gets its own message (owner, 2026-09-30);
    // the generic "fill in …" would not say what is wrong with a field the
    // trader did fill in. Checked on the RAW review draft: buildIdeaRequestDraft
    // drops a non-plain quantity, so normalizedDraft would read it as empty.
    // Applies to both idea modes, publish and team_search.
    if (ideaQuantityInvalid(reviewDraft || {})) {
      svc.fieldError('quantity'); setError(quantityMessage(lang));
      return;
    }
    if (ideaDraftMissing(normalizedDraft).length > 0) {
      svc.fieldError('draft'); setError(t.requiredDraft);
      return;
    }

    if (!user) {
      persistFlowDraft({ nextReviewDraft: normalizedDraft, nextPhase: 'report', pendingAuth: true });
      if (onClose) onClose();
      nav('/login/buyer');
      return;
    }

    if (ideaMode !== 'publish' && ideaMode !== 'team_search') {
      svc.fieldError('idea_mode'); setError(mt.required);
      return;
    }
    const isTeamSearch = ideaMode === 'team_search';
    // team_search: a WhatsApp-verified number. publish: a valid number, as before.
    if (isTeamSearch) {
      if (!verifiedPhone) {
        svc.fieldError('phone_verify'); setError(t.errPhoneVerify);
        return;
      }
    } else if (ideaDraftMissing(normalizedDraft, { phoneValid: phone.valid }).includes('phone')) {
      svc.fieldError('phone'); setError(t.errPhone);
      return;
    }
    const contactPhone = isTeamSearch ? verifiedPhone : phone.e164;

    setSubmitting(true);
    setError('');

    // Upload BEFORE the insert so the paths go in with the row. uploadAll never
    // throws: a file that does not make it is reported, the request is created
    // regardless, and the trader is told what to re-add (decision §4.1).
    const { paths: attachmentPaths, failed } = await picker.uploadAll(user.id);

    const rawDescription = normalizedDraft.description || result?.request_description || result?.specs || '';
    const rawTitleAr = normalizedDraft.title_ar || '';
    const rawTitleEn = normalizedDraft.title_en || '';

    // ═══ REMOVED 2026-09-24 — the client-side translation call ═══════════════
    // buildTranslatedRequestFields() ran here, awaited, before the insert. It is
    // the pattern Requests.jsx removed on 2026-09-21 with a DO-NOT-REINTRODUCE
    // note: it decides the source language by FIELD, not by script, and it
    // held two AI round-trips between the tap and the insert. The SERVER owns
    // this: trg_requests_enqueue_translation (20260921112056) calls
    // translate-request on every insert, which detects the script, fills all
    // six columns and records requests.translation_status. The row goes in
    // with the source text, exactly as the direct form's does.
    // ⚠️ DO NOT REINTRODUCE A CLIENT TRANSLATION CALL HERE.
    // ══════════════════════════════════════════════════════════════════════════

    const payload = {
      buyer_id: user.id,
      title_ar: rawTitleAr || rawTitleEn,
      title_en: rawTitleEn || rawTitleAr,
      title_zh: normalizedDraft.title_zh || rawTitleEn || rawTitleAr,
      // A plain number, with the unit in its own column — as the direct form
      // writes them. ideaDraftMissing has already refused anything that is not
      // isPlainQuantity, and buildIdeaRequestDraft trimmed it.
      quantity: normalizedDraft.quantity,
      unit: normalizedDraft.unit || null,
      description: rawDescription,
      description_ar: rawDescription || null,
      description_en: rawDescription || null,
      description_zh: rawDescription || null,
      category: normalizedDraft.category || result?.category || 'other',
      status: 'open',
      budget_per_unit: normalizedDraft.budget_per_unit ? parseFloat(normalizedDraft.budget_per_unit) : null,
      budget_currency: normalizedDraft.budget_per_unit ? 'SAR' : null,   // traders budget in SAR only (owner, 2026-09-29)
      payment_plan: normalizedDraft.payment_plan ? parseInt(normalizedDraft.payment_plan, 10) : null,
      sample_requirement: normalizedDraft.sample_requirement || null,
      // Destination + delivery ask — what a factory prices freight against.
      // KSA-only city list here, so the country is a constant (as on the
      // direct form and the /request wizard).
      ship_city: normalizedDraft.ship_city || null,
      ship_country: normalizedDraft.ship_city ? 'Saudi Arabia' : null,
      delivery_preference: normalizedDraft.delivery_preference || null,
      // team_search: the WhatsApp-verified number (owner, 2026-10-01).
      // publish: validated E.164 from PhoneField. Submit is blocked without it.
      contact_phone: contactPhone,
      // PRIVATE request-attachments object paths, which /f/<slug> signs. The
      // public reference_image dual-write was retired here; this page never had
      // an uploader to fill it, so the value was always null in practice.
      attachment_paths: attachmentPaths,
      attachments_missing: failed.length > 0,
      // CHANGED 2026-09-29 — the trader chooses (owner):
      //  • team_search: managed, tagged idea_to_product so it enters the
      //    Concierge pipeline (decisions #1, #8). Carries the request fee —
      //    the server attaches it on insert.
      //  • publish: a plain RFQ. request_kind 'direct' + sourcing_mode
      //    'direct' make every existing RFQ predicate treat it as one (offer
      //    visibility, delete rules, supplier lists, both apps' shipped
      //    builds); idea_mode 'publish' keeps where it came from.
      ...(isTeamSearch ? {
        sourcing_mode: 'managed',
        request_kind: 'idea_to_product',
        idea_mode: 'team_search',
        managed_status: 'submitted',
        managed_review_state: 'pending',
      } : {
        sourcing_mode: 'direct',
        request_kind: 'direct',
        idea_mode: 'publish',
      }),
    };

    const { data, error: requestError } = await runWithOptionalColumns({
      table: 'requests',
      payload,
      // attachment_paths / attachments_missing ride the same fallback: an
      // environment a migration behind strips them rather than losing the
      // request.
      optionalKeys: ['description_ar', 'description_en', 'description_zh', 'request_kind', 'attachment_paths', 'attachments_missing', 'unit', 'contact_phone', 'ship_city', 'ship_country', 'delivery_preference'],
      execute: (nextPayload) => sb.from('requests').insert(nextPayload).select('id').single(),
    });
    setSubmitting(false);
    if (requestError) {
      // The server's verified-phone rule: back to the phone step with the
      // matching message, not the generic error.
      const hint = requestPhoneRefusal(requestError);
      if (hint) {
        setPhoneRefusal((r) => ({ hint, n: (r?.n || 0) + 1 }));
        setError(phoneRefusalMessage(hint, lang));
        svc.fieldError('phone_server');
        return;
      }
      setError(t.error);
      return;
    }
    svc.submitted({ request_id: data?.id, fee_required: isTeamSearch });
    // Set only after the insert succeeded: these names are a caveat ON a created
    // request, never a failure report in their own right.
    setFailedFiles(failed.map((f) => f.name));

    // First phone this trader has given us → their profile, so the next
    // request prefills. Blank profiles only; never overwrites. Fire-and-forget.
    saveProfilePhoneIfBlank({ userId: user.id, profile, e164: contactPhone })
      .then((saved) => { if (saved) setProfile?.((p) => (p ? { ...p, phone: contactPhone } : p)); });

    // Managed brief → admin_review so the idea request lands in Concierge like
    // other managed requests. Fire-and-forget: never blocks the success nav.
    // requestId is what makes maabar-ai persist the brief SERVER-side, so it no
    // longer depends on this tab staying open (it was missing here, and only
    // here — Requests.jsx and createManagedRequest both pass it). The client
    // upsert below stays as the fallback, as it does on the direct form.
    // Managed brief: the team_search path only — a published idea is an RFQ.
    if (data?.id && isTeamSearch) {
      (async () => {
        try {
          const brief = await generateManagedBriefWithAI({ request: payload, lang, requestId: data.id, buyerId: user.id });
          await sb.from('managed_request_briefs').upsert(
            buildManagedBriefRow({ requestId: data.id, buyerId: user.id, brief }),
            { onConflict: 'request_id' },
          );
          await sb.from('requests').update({
            managed_status: 'admin_review',
            managed_priority: brief.priority || 'normal',
            managed_ai_ready_at: new Date().toISOString(),
          }).eq('id', data.id);
        } catch (briefErr) {
          console.error('[IdeaToProduct] managed brief setup error:', briefErr?.message || briefErr);
        }
      })();
    }

    clearIdeaFlowDraft();
    picker.clearPersisted();
    try {
      window.sessionStorage.removeItem('maabar_request_draft');
    } catch {}

    // team_search: the request fee, straight to Telr. The request is saved;
    // nothing downstream acts on it until the fee is paid. If the payment page
    // cannot open, the trader lands on their requests, where the fee can be
    // paid (the card shows it).
    if (data?.id && isTeamSearch) {
      try {
        setSubmitting(true);
        if (await payFeeIfRequired(sb, data.id)) return;   // leaving for Telr
      } catch (feeErr) {
        console.error('[IdeaToProduct] could not start the request fee payment:', feeErr?.message || feeErr);
        // Not silent: the trader must know the fee is still unpaid before the
        // navigation below carries them to their requests.
        alert(isAr
          ? 'تم حفظ طلبك، لكن تعذّر فتح صفحة الدفع. لم يُخصم أي مبلغ — تقدر تدفع رسوم تأكيد الطلب من «طلباتي».'
          : "Your request is saved, but the payment page couldn't open. Nothing was charged — you can pay the request confirmation fee from My requests.");
      } finally {
        setSubmitting(false);
      }
    }
    // One confirmation for every request type (owner, 2026-10-03).
    appendMessage('assistant', `${requestReceivedCopy(lang).title}\n${requestReceivedText(lang)}`);
    if (onClose) onClose();
    nav(data?.id ? `/dashboard?tab=requests&request=${data.id}` : '/dashboard?tab=requests');
  };

  // Signed-in traders only (owner, 2026-10-22: «صنّع فكرتك للمسجلين دخول
  // فقط»). A visitor gets this card instead of the chat, so no AI call is made
  // for them; after sign-in, AIHub reopens the tool from ?openIdea=1. Every
  // door renders this component (AIHub, Home.jsx), so this is the one gate.
  // Mirror of the app's IdeaToProductScreen.
  if (!user) {
    const g = IDEA_GATE[lang] || IDEA_GATE.ar;
    const goLogin = (signup) => {
      const back = new URLSearchParams(window.location.search);
      back.set('openIdea', '1');
      const next = encodeURIComponent(`${window.location.pathname}?${back.toString()}`);
      if (onClose) onClose();
      nav(`/login/buyer?next=${next}${signup ? '&mode=signup' : ''}`);
    };
    const font = isAr ? 'var(--font-ar)' : 'var(--font-sans)';
    return (
      <div role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, background: 'rgba(4,4,6,0.72)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18 }}>
        <div dir={isAr ? 'rtl' : 'ltr'} style={{ width: '100%', maxWidth: 420, background: 'var(--surface-raised)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', padding: '28px 24px', textAlign: 'center', fontFamily: font }}>
          <p style={{ fontSize: 19, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.6, marginBottom: 10 }}>{g.title}</p>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.9, marginBottom: 22 }}>{g.body}</p>
          <button type="button" className="btn-primary" onClick={() => goLogin(false)} style={{ width: '100%', minHeight: 48, marginBottom: 10, fontFamily: font }}>{g.signIn}</button>
          <button type="button" className="btn-outline" onClick={() => goLogin(true)} style={{ width: '100%', minHeight: 48, marginBottom: 12, fontFamily: font }}>{g.signUp}</button>
          <button type="button" onClick={() => { if (onClose) onClose(); }} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', textDecoration: 'underline', cursor: 'pointer', minHeight: 44, fontFamily: font }}>{g.back}</button>
        </div>
      </div>
    );
  }

  if (minimized) {
    return (
      <div
        onClick={() => setMinimized(false)}
        style={{
          position: 'fixed', bottom: 'calc(24px + env(safe-area-inset-bottom, 0px))', left: 24, zIndex: 2000,
          background: 'var(--surface-raised)', color: 'var(--text-primary)', border: '1px solid var(--border)',
          padding: '12px 18px', borderRadius: 'var(--radius-card)', cursor: 'pointer', fontSize: 12,
          display: 'flex', alignItems: 'center', gap: 10,
          fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
        }}
      >
        <span style={{ width: 8, height: 8, borderRadius: 'var(--radius-full)', background: 'var(--ink)', display: 'inline-block' }} />
        {t.minimized}
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', top: isMobile ? 'var(--vv-top)' : 0, left: 0, right: 0, height: isMobile ? 'var(--app-dvh)' : '100dvh', background: 'rgba(4,4,6,0.72)', zIndex: 2000, display: 'flex', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'center', padding: isMobile ? 0 : 18, overflow: 'hidden' }}>
      <style>{`@keyframes maabarTyping { 0%, 80%, 100% { transform: translateY(0); opacity: .35; } 40% { transform: translateY(-3px); opacity: 1; } }`}</style>
      <div style={{ width: '100%', maxWidth: isMobile ? '100%' : 760, height: isMobile ? '100%' : 'min(88vh, 860px)', background: 'var(--surface-raised)', border: '1px solid var(--border)', borderRadius: isMobile ? 0 : 'var(--radius-card)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '20px 24px', background: 'var(--bg-page)', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14 }}>
          <div>
            <p style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{t.title}</p>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.7, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{t.subtitle}</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setMinimized(true)} style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: 14, cursor: 'pointer', padding: '4px 12px', borderRadius: 'var(--radius-panel)' }}>—</button>
            <button onClick={() => { if (phase === 'report') persistFlowDraft({ nextPhase: 'report' }); else clearIdeaFlowDraft(); if (onClose) onClose(); }} style={{ background: 'none', border: 'none', color: 'var(--text-primary)', fontSize: 24, cursor: 'pointer', lineHeight: 1 }}>×</button>
          </div>
        </div>

        <div ref={bodyRef} style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: isMobile ? '18px 14px' : '24px 22px', display: 'flex', flexDirection: 'column', gap: 14, background: 'var(--bg-page)', direction: isAr ? 'rtl' : 'ltr' }}>
          {messages.map((message) => (
            <Bubble key={message.id} role={message.role} isAr={isAr}>{message.content}</Bubble>
          ))}

          {isTyping && <TypingBubble />}

          {phase === 'report' && result && (
            <div style={{ marginTop: 8, padding: 18, background: 'var(--surface-raised)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)' }}>
              <p style={{ fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 16 }}>{t.report}</p>
              <ReportRow label={t.reportFields.product} value={lang === 'zh' ? result.product_name_zh : (isAr ? result.product_name_ar : result.product_name_en)} isAr={isAr} />
              <ReportRow label={t.reportFields.factory} value={result.factory_type} isAr={isAr} />
              <ReportRow label={t.reportFields.city} value={result.city} isAr={isAr} />
              <ReportRow label={t.reportFields.moq} value={result.moq} isAr={isAr} />
              <ReportRow label={t.reportFields.timeline} value={result.timeline} isAr={isAr} />
              <ReportRow label={t.reportFields.category} value={getSpecialtyLabel(result.category, lang)} isAr={isAr} />
              <div style={{ paddingTop: 14 }}>
                <p style={{ color: 'var(--text-secondary)', fontSize: 12, marginBottom: 8, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{t.reportFields.specs}</p>
                <div style={{ padding: '12px 14px', background: 'var(--bg-page)', border: '1px solid var(--border)', borderRadius: 'var(--radius-panel)', color: 'var(--text-primary)', fontSize: 13, lineHeight: 1.9, whiteSpace: 'pre-wrap', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                  {result.specs || result.request_description}
                </div>
              </div>

              <div style={{ marginTop: 18, paddingTop: 18, borderTop: '1px solid var(--border)' }}>
                <p style={{ fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 10 }}>{t.review}</p>
                <p style={{ color: 'var(--text-secondary)', fontSize: 12, lineHeight: 1.8, marginBottom: 16, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{t.reviewHint}</p>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                  <div>
                    <FormLabel isAr={isAr}>{t.reviewFields.titleAr}</FormLabel>
                    <FieldInput isAr={isAr} value={reviewDraft.title_ar || ''} onChange={(event) => updateReviewDraft('title_ar', event.target.value)} />
                  </div>
                  <div>
                    <FormLabel isAr={isAr}>{t.reviewFields.titleEn}</FormLabel>
                    <FieldInput isAr={isAr} value={reviewDraft.title_en || ''} onChange={(event) => updateReviewDraft('title_en', event.target.value)} dir="ltr" />
                  </div>
                  <div>
                    <FormLabel isAr={isAr}>{t.reviewFields.quantity}</FormLabel>
                    {/* A NUMBER plus a UNIT, as on the direct form. The AI's MOQ
                        used to be prefilled here as prose; it is a hint now. */}
                    <div style={{ display: 'flex', gap: 6 }}>
                      <FieldInput isAr={isAr} inputMode="decimal" dir="ltr" value={reviewDraft.quantity || ''}
                        onChange={(event) => updateReviewDraft('quantity', normalizeQuantityInput(event.target.value))}
                        placeholder={isAr ? 'مثال: 500' : lang === 'zh' ? '例：500' : 'e.g. 500'}
                        style={{ flex: 1, direction: 'ltr' }} />
                      <select
                        value={reviewDraft.unit || ''}
                        onChange={(event) => updateReviewDraft('unit', event.target.value)}
                        style={{
                          width: 110,
                          background: 'var(--bg-page)',
                          color: 'var(--text-primary)',
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-panel)',
                          padding: '12px 10px',
                          fontSize: 14,
                          outline: 'none',
                          fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
                        }}
                      >
                        <option value="">{t.unitPlaceholder}</option>
                        {REQUEST_UNITS.map((u) => (<option key={u.key} value={u.key}>{labelFor(u, lang)}</option>))}
                      </select>
                    </div>
                    {result?.moq && (
                      <p style={{ color: 'var(--text-secondary)', fontSize: 11, lineHeight: 1.7, marginTop: 6, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                        {t.moqHint} {result.moq}
                      </p>
                    )}
                  </div>
                  <div>
                    <FormLabel isAr={isAr}>{t.budgetOptional}</FormLabel>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <FieldInput isAr={isAr} type="number" min="0" value={reviewDraft.budget_per_unit || ''} onChange={(event) => updateReviewDraft('budget_per_unit', event.target.value)} dir="ltr" style={{ flex: 1 }} />
                      {/* SAR only (owner, 2026-09-29): a trader never works in USD/CNY. */}
                      <span
                        style={{
                          width: 90,
                          background: 'var(--bg-page)',
                          color: 'var(--text-primary)',
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-panel)',
                          padding: '12px 10px',
                          fontSize: 14,
                          direction: 'ltr',
                          fontFamily: 'var(--font-sans)',
                          textAlign: 'center',
                        }}
                      >SAR</span>
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 12 }}>
                  <FormLabel isAr={isAr}>{t.reviewFields.category}</FormLabel>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {(UI_CATEGORIES[lang] || UI_CATEGORIES.en).filter(c => c.val !== 'all').map(({ val: value, label }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => updateReviewDraft('category', value)}
                        style={{
                          padding: '7px 14px',
                          fontSize: 12,
                          borderRadius: 'var(--radius-pill)',
                          cursor: 'pointer',
                          background: reviewDraft.category === value ? 'var(--ink)' : 'transparent',
                          color: reviewDraft.category === value ? 'var(--on-dark)' : 'var(--text-secondary)',
                          border: '1px solid var(--border)',
                          fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ marginTop: 12 }}>
                  <FormLabel isAr={isAr}>{t.reviewFields.description}</FormLabel>
                  <FieldInput isAr={isAr} as="textarea" rows={5} value={reviewDraft.description || ''} onChange={(event) => updateReviewDraft('description', event.target.value)} />
                  <p style={{ fontSize: 11.5, color: 'var(--text-disabled)', lineHeight: 1.7, margin: '6px 2px 0', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{requestContactNote(lang)}</p>
                </div>

                {/* Attachments — the PRIVATE bucket. Optional: nothing here can
                    block the submit, and a failed upload is reported after the
                    request exists, never instead of it.
                    MOVED 2026-09-28 (owner: "the image upload isn't visible"): it
                    was the LAST field of a form ~1,700px tall (desktop) / ~2,300px
                    (mobile) against ~700px visible, so it appeared only after
                    scrolling past everything else. It sits with the description
                    now — reference images belong with the product they show. */}
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                    {t.attachLabel}
                  </div>
                  <p style={{ color: 'var(--text-secondary)', fontSize: 11, lineHeight: 1.7, margin: '2px 0 8px', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                    {t.attachHint}
                  </p>
                  {/* A real button, not a faint outline: it was 12px text with no fill
                      and a 1px #E8E5DE border on white (~1.2:1) — measured in a
                      browser on 2026-09-28, it rendered but read as plain text. */}
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 44, cursor: 'pointer', background: 'var(--bg-page)', color: 'var(--text-primary)', border: '1px dashed var(--text-secondary)', borderRadius: 'var(--radius-panel)', padding: '10px 16px', fontSize: 13, fontWeight: 600, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                    <span aria-hidden="true" style={{ fontSize: 16, lineHeight: 1 }}>＋</span>
                    {t.attachPick}
                    <input type="file" multiple accept={ATTACH_ACCEPT} style={{ display: 'none' }}
                      onChange={(event) => { picker.addFiles(event.target.files); event.target.value = ''; }} />
                  </label>

                  {picker.files.length > 0 && (
                    <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0' }}>
                      {picker.files.map((f) => (
                        <li key={f.id} style={{
                          display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0', fontSize: 12,
                          flexDirection: isAr ? 'row-reverse' : 'row',
                          fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
                        }}>
                          <span dir="ltr" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                          <button type="button" onClick={() => picker.removeFile(f.id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', fontSize: 11, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                            {t.attachRemove}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}

                  {picker.error && <Notice tone="error" isAr={isAr}>{picker.error}</Notice>}
                </div>


                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginTop: 12 }}>
                  <div>
                    <FormLabel isAr={isAr}>{t.reviewFields.paymentPlan}</FormLabel>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {Object.entries(PAYMENT_PLAN_LABEL[lang] || PAYMENT_PLAN_LABEL.en).map(([value, label]) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => updateReviewDraft('payment_plan', value)}
                          style={{
                            padding: '7px 12px',
                            fontSize: 12,
                            borderRadius: 'var(--radius-pill)',
                            cursor: 'pointer',
                            background: String(reviewDraft.payment_plan) === String(value) ? 'var(--ink)' : 'transparent',
                            color: String(reviewDraft.payment_plan) === String(value) ? 'var(--on-dark)' : 'var(--text-secondary)',
                            border: '1px solid var(--border)',
                            fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
                          }}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <FormLabel isAr={isAr}>{t.reviewFields.sampleRequirement}</FormLabel>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {Object.entries(SAMPLE_LABEL[lang] || SAMPLE_LABEL.en).map(([value, label]) => (
                        <label key={value} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 12, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                          <input type="radio" name="idea-sample-requirement" checked={reviewDraft.sample_requirement === value} onChange={() => updateReviewDraft('sample_requirement', value)} style={{ accentColor: 'var(--ink)' }} />
                          {label}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Destination + delivery ask (2026-09-27). Same lists, same
                    stored values as the direct form and the /request wizard:
                    KSA_CITIES stores the ENGLISH city name in every language
                    (it travels to a Chinese factory), DELIVERY_PREFERENCES the
                    three keys the column's CHECK accepts. Nothing preselected. */}
                <div style={{ marginTop: 12 }}>
                  <FormLabel isAr={isAr}>{t.reviewFields.shipCity}</FormLabel>
                  <CityPicker value={reviewDraft.ship_city || ''} onChange={(v) => updateReviewDraft('ship_city', v)} lang={lang}
                    style={{ fontSize: 14 }} />
                </div>

                <div style={{ marginTop: 12 }}>
                  <FormLabel isAr={isAr}>{t.reviewFields.delivery}</FormLabel>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {DELIVERY_PREFERENCES.map((d) => {
                      const on = reviewDraft.delivery_preference === d.key;
                      return (
                        <label key={d.key} style={{
                          display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
                          padding: '10px 12px', borderRadius: 'var(--radius-panel)',
                          border: '1px solid var(--border)', background: on ? 'var(--surface-raised)' : 'transparent',
                          fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
                        }}>
                          <input type="radio" name="idea-delivery-preference" checked={on}
                            onChange={() => updateReviewDraft('delivery_preference', d.key)}
                            style={{ accentColor: 'var(--ink)', marginTop: 3 }} />
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ display: 'block', fontSize: 13, color: 'var(--text-primary)' }}>{labelFor(d, lang)}</span>
                            <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 2, lineHeight: 1.6 }}>{deliveryHintFor(d, lang)}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* The two options (owner, 2026-09-29). Required — no default. */}
                <div style={{ marginTop: 16 }} role="radiogroup" aria-label={mt.heading}>
                  <FormLabel isAr={isAr}>{mt.heading}</FormLabel>
                  {[
                    { key: 'publish', title: mt.publish, price: mt.publishPrice, body: mt.publishBody },
                    {
                      key: 'team_search', title: mt.team, body: mt.teamBody,
                      price: feeConfig?.enabled && Number(feeConfig.amount) > 0
                        ? (isAr ? `${Number(feeConfig.amount)} ر.س` : `SAR ${Number(feeConfig.amount)}`) : '',
                    },
                  ].map((opt) => {
                    const on = ideaMode === opt.key;
                    return (
                      <button
                        key={opt.key} type="button" role="radio" aria-checked={on}
                        onClick={() => { setIdeaMode(opt.key); setError(''); }}
                        style={{
                          display: 'block', width: '100%', textAlign: isAr ? 'right' : 'left', marginTop: 8,
                          padding: '12px 14px', borderRadius: 'var(--radius-panel)', cursor: 'pointer',
                          background: on ? 'var(--bg-raised)' : 'var(--bg-page)',
                          border: on ? '1.5px solid var(--ink)' : '1px solid var(--border)',
                          color: 'var(--text-primary)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
                        }}
                      >
                        <span style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 13, fontWeight: 700 }}>
                          <span>{opt.title}</span>
                          {opt.price ? <span style={{ direction: 'ltr', whiteSpace: 'nowrap' }}>{opt.price}</span> : null}
                        </span>
                        <span style={{ display: 'block', fontSize: 11.5, lineHeight: 1.7, marginTop: 4, color: 'var(--text-secondary)' }}>{opt.body}</span>
                      </button>
                    );
                  })}
                  {ideaMode === 'team_search' && <RequestFeeNotice lang={lang === 'ar' ? 'ar' : 'en'} config={feeConfig} style={{ marginTop: 10 }} />}
                </div>

                {/* Contact phone — signed-in only; see ideaDraftMissing. Below the mode
                    choice since 2026-10-01: team_search swaps in the verified field. */}
                {user && (
                  <div style={{ marginTop: 16 }}>
                    <FormLabel isAr={isAr}>{t.phoneLabel}</FormLabel>
                    {ideaMode === 'team_search' ? (
                      <VerifiedPhoneField
                        value={phone.e164 || profile?.phone || ''}
                        onVerified={setVerifiedPhone}
                        onNumberChange={(e164) => { phoneTouched.current = true; setPhone(parseStoredPhone(e164)); }}
                        lang={lang}
                        profile={profile}
                        setProfile={setProfile}
                        refusal={phoneRefusal}
                        selectStyle={{ background: 'var(--bg-page)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-panel)', padding: '12px 8px', fontSize: 14, outline: 'none', fontFamily: 'var(--font-sans)' }}
                        inputStyle={{ background: 'var(--bg-page)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-panel)', padding: '12px 14px', fontSize: 16, outline: 'none', fontFamily: 'var(--font-sans)', minWidth: 0 }}
                      />
                    ) : (
                      <PhoneField
                        key={phoneKey}
                        value={phone.e164}
                        onChange={(e164, valid) => { phoneTouched.current = true; setPhone({ e164, valid }); }}
                        defaultCountry="SA"
                        lang={lang}
                        invalid={Boolean(phone.e164) && !phone.valid}
                        selectStyle={{ background: 'var(--bg-page)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-panel)', padding: '12px 8px', fontSize: 14, outline: 'none', fontFamily: 'var(--font-sans)' }}
                        inputStyle={{ background: 'var(--bg-page)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-panel)', padding: '12px 14px', fontSize: 16, outline: 'none', fontFamily: 'var(--font-sans)', minWidth: 0 }}
                      />
                    )}
                    <p style={{ color: 'var(--text-secondary)', fontSize: 11, lineHeight: 1.7, marginTop: 6, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                      {t.phoneHint}
                    </p>
                  </div>
                )}

                {ideaMode !== 'team_search' && (
                  <p style={{ color: 'var(--text-secondary)', fontSize: 11, lineHeight: 1.8, marginTop: 14, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                    {t.requestPreviewNote}
                  </p>
                )}
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
                <button onClick={submitRequest} disabled={submitting || (Boolean(user) && ideaMode === 'team_search' && !verifiedPhone)} style={{ flex: 1, minWidth: 180, background: 'var(--ink)', color: 'var(--on-dark)', border: 'none', padding: '14px 16px', borderRadius: 'var(--radius-panel)', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', opacity: (submitting || (Boolean(user) && ideaMode === 'team_search' && !verifiedPhone)) ? 0.65 : 1 }}>
                  {user ? t.submit : t.loginSubmit}
                </button>
                <button onClick={resetAll} style={{ flex: 1, minWidth: 160, background: 'transparent', color: 'var(--text-primary)', border: '1px solid var(--border)', padding: '14px 16px', borderRadius: 'var(--radius-panel)', fontSize: 13, cursor: 'pointer', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                  {t.newChat}
                </button>
              </div>
            </div>
          )}

          {phase === 'generating' && (
            <Bubble role="assistant" isAr={isAr}>{t.generating}</Bubble>
          )}

          {error && <Notice tone="error" isAr={isAr}>{error}</Notice>}
          {/* A caveat on a CREATED request, so warn rather than error, and it
              sits beside the confirmation rather than replacing it. */}
          {failedFiles.length > 0 && (
            <Notice tone="warn" isAr={isAr}>
              {t.warnUpload(failedFiles.join(isAr ? '، ' : ', '))}
            </Notice>
          )}
        </div>

        {phase !== 'report' && (
          <div style={{ padding: isMobile ? '14px 12px calc(14px + env(safe-area-inset-bottom))' : 18, borderTop: '1px solid var(--border)', background: 'var(--surface-raised)' }}>
            {/* Files attached in the chat (2026-09-29, owner): the assistant tells
                the trader they can upload from "the attachments button here", and
                until now there was none in the chat — only on the review screen.
                This is the SAME `picker` (useRequestAttachments) the review screen
                renders and submitRequest uploads, so a file picked here shows up on
                the review screen and lands in requests.attachment_paths with the
                request — nothing is copied or re-picked between the two. */}
            {picker.files.length > 0 && (
              <div dir={isAr ? 'rtl' : 'ltr'} style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10, alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                  {t.attachedInChat(picker.files.length)}
                </span>
                {picker.files.map((f) => (
                  <span key={f.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: 220, border: '1px solid var(--border)', borderRadius: 999, padding: '4px 10px', fontSize: 11, background: 'var(--bg-page)' }}>
                    <span dir="ltr" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                    <button type="button" onClick={() => picker.removeFile(f.id)} aria-label={t.attachRemove}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', fontSize: 13, lineHeight: 1, padding: 0 }}>×</button>
                  </span>
                ))}
              </div>
            )}
            {picker.error && <Notice tone="error" isAr={isAr}>{picker.error}</Notice>}
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
              <label title={t.attachChat} aria-label={t.attachChat}
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto', width: 48, height: 48, cursor: 'pointer', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', background: 'var(--bg-page)', color: 'var(--text-primary)', fontSize: 20 }}>
                <span aria-hidden="true">📎</span>
                <input type="file" multiple accept={ATTACH_ACCEPT} style={{ display: 'none' }}
                  onChange={(event) => { picker.addFiles(event.target.files); event.target.value = ''; }} />
              </label>
              <textarea
                value={draftText}
                onChange={(event) => setDraftText(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    handleSend();
                  }
                }}
                onFocus={() => setTimeout(() => {
                  if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
                }, 250)}
                rows={2}
                placeholder={t.placeholder}
                style={{
                  flex: 1,
                  resize: 'none',
                  background: 'var(--bg-page)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-card)',
                  padding: '14px 16px',
                  outline: 'none',
                  fontSize: 16,
                  lineHeight: 1.8,
                  fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
                  direction: isAr ? 'rtl' : 'ltr',
                }}
              />
              <button onClick={handleSend} disabled={!draftText.trim() || isTyping} style={{ minWidth: 112, background: draftText.trim() && !isTyping ? 'var(--ink)' : 'var(--border)', color: draftText.trim() && !isTyping ? 'var(--on-dark)' : 'var(--text-disabled)', border: 'none', padding: '14px 18px', borderRadius: 'var(--radius-card)', fontSize: 13, fontWeight: 700, cursor: draftText.trim() && !isTyping ? 'pointer' : 'default', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                {t.send}
              </button>
            </div>
            {briefReady && (
              <button
                onClick={generateReport}
                disabled={isTyping}
                style={{ marginTop: 10, width: '100%', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border)', padding: '12px 14px', borderRadius: 'var(--radius-card)', fontSize: 12, cursor: isTyping ? 'default' : 'pointer', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', opacity: isTyping ? 0.5 : 1 }}>
                {isAr ? 'حوّل المحادثة إلى تقرير احترافي' : lang === 'zh' ? '将对话整理成专业报告' : 'Turn this conversation into a professional brief'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
